import React, { useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import './sidebar-style.scss'
import { styled } from '@mui/material/styles'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import LogoutIcon from '@mui/icons-material/Logout'
import usePermission from '../../hooks/usePermission'
import useAppRoutes from '../../routes/useAppRoutes'
import LogoP1 from '../../assets/images/DE_logo.png'
import { AuthContext } from '../../routes/AuthContext'
import { removeUserType, removeToken } from '../../redux/reducers/authReducer'
import { useDispatch } from 'react-redux'

const drawerWidth = 240

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  padding: theme.spacing(0, 1),
  backgroundColor: 'white',
  ...theme.mixins.toolbar,
}))

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
  zIndex: theme.zIndex.drawer
}))

const SideBar = ({ open, handleDrawerClose, handleDrawerOpen }) => {
  const appRoutes = useAppRoutes()
  const navigate = useNavigate()
  const location = useLocation()
  const { is, can } = usePermission() // Hook for RBAC

  const handleNavClick = (route) => {
    navigate(route.path)
  }
  const [selectedIndex, setSelectedIndex] = React.useState(1)
  const [nestopen, setOpen] = React.useState(false)
  const { setToken } = useContext(AuthContext)

  const handleListItemClick = (event, index) => {
    setSelectedIndex(index)
  }

  const dispatch = useDispatch()

  const handleLogout = () => {
    localStorage.removeItem('accessToken')
    const storedRememberMe = localStorage.getItem('rememberMeChecked')
    if (storedRememberMe === 'false') {
      localStorage.removeItem('encryptedUsername')
      localStorage.removeItem('encryptedPassword')
    }

    setToken(null)
    dispatch(removeUserType())
    dispatch(removeToken())
    navigate('/', { state: { logoutSuccess: true } })
  }

  const renderListItem = (route, index) => {
    // RBAC Visibility Logic
    let isVisible = false;


    if (is('Super Admin') || is('Admin')) {
      isVisible = true;
    } else if (route.permissions && route.permissions.length > 0) {
      isVisible = route.permissions.some(p => can(p));
    } else {
      // If no permissions defined on route, default to visible if listed in side nav
      isVisible = true;
    }

    if (!isVisible) return null


    if (!route.listInSideNav) return
    if (route.path === `/${location.pathname.split('/')[1]}`) {
      if (index !== selectedIndex) setSelectedIndex(index)
    }
    const navClick = (event) => {
      handleNavClick(route)
      handleListItemClick(event, index)
    }

    return (
      <ListItem
        key={route.key}
        disablePadding
        sx={{ display: 'block', backgroundColor: 'white' }}
        selected={selectedIndex === index}
        onClick={(event) => navClick(event)}
      >
        <ListItemButton
          style={
            selectedIndex === index
              ? {
                backgroundColor: 'rgba(89, 104, 183, 0.25)',
                borderRadius: '6px',
              }
              : {}
          }
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            margin: 1,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
            }}
          >
            {route.sideNavIcon}
          </ListItemIcon>
          <ListItemText
            primary={route.name}
            sx={{
              opacity: open ? 1 : 0,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: 'Poppins',
              color: '#464646',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const multiLevelrenderListItem = (route, index) => {
    if (!route.listInSideNav) return
    const handleClick = () => {
      setOpen(!nestopen)
    }

    const navClick = (event) => {
      handleClick()
      handleListItemClick(event, index)
    }

    const nestednavClick = (event, data) => {
      handleNavClick(data)
      handleListItemClick(event, index)
    }

    const DATA = route.nestedChildren

    return (
      <ListItem
        key={route.key}
        disablePadding
        sx={{ display: 'block', backgroundColor: 'white' }}
        selected={selectedIndex === index}
        onClick={(event) => navClick(event)}
        onMouseOver={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="m6"
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 2 : 'auto',
              justifyContent: 'center',
            }}
          >
            {route.sideNavIcon}
          </ListItemIcon>
          <ListItemText
            primary={route.name}
            sx={{
              opacity: open ? 1 : 0,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: 'Poppins',
              color: '#464646',
            }}
          />
        </ListItemButton>
        <Collapse in={nestopen} timeout="auto" unmountOnExit>
          {Object.keys(DATA).map((key) => {
            const nestedroute = DATA[key]
            return (
              <ListItem
                key={nestedroute.key}
                onClick={(event) => nestednavClick(event, nestedroute)}
                className="m6"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {nestedroute.sideNavIcon}
                  </ListItemIcon>
                  <ListItemText
                    primary={nestedroute.name}
                    sx={{
                      opacity: open ? 1 : 0,
                      fontSize: 15,
                      fontWeight: 500,
                      fontFamily: 'Poppins',
                      color: '#464646',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
          {/* </List> */}
        </Collapse>
      </ListItem>
    )
  }

  return (
    <Drawer
      id="sidebar"
      variant="permanent"
      open={open}
      sx={{ backgroundColor: 'white' }}
    >
      <DrawerHeader className="align_start">
        <IconButton
          onClick={handleDrawerClose}
          sx={{ position: 'absolute', left: 5 }}
        >
          <div
            style={{
              width: open ? 70 : 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={LogoP1}
              alt="Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        </IconButton>
        {open}
      </DrawerHeader>
      <List
        onMouseOver={handleDrawerOpen}
        onMouseLeave={handleDrawerClose}
        sx={{ backgroundColor: 'white', height: '100%' }}
      >
        {appRoutes &&
          Object.keys(appRoutes).map((key, index) =>
            !appRoutes[key].hasChildren
              ? renderListItem(appRoutes[key], index)
              : multiLevelrenderListItem(appRoutes[key], index)
          )}

        <ListItem className="signout_pos" onClick={handleLogout}>
          <ListItemButton className="sign_out">
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>
              <span>Signout</span>
            </ListItemText>
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  )
}

export default SideBar
